/**
 * Helper functions for building AI context data objects
 * 
 * These helpers reduce code duplication across template selection flows
 * by providing consistent context data building patterns.
 */

/**
 * Build context data from an encounter option (used when selecting from dropdown)
 * @param {Object} encounterOption - The encounter option from the dropdown
 * @returns {Object} Context data with encounter_id, patient_id, patient_name
 */
export function buildContextFromEncounterOption(encounterOption) {
  if (!encounterOption) return {};
  
  const context: Record<string, unknown> = {
    encounter_id: encounterOption.encounter_id,
    patient_id: encounterOption.patient_id || null,
    patient_name: encounterOption.patient_name || null,
  };

  if (encounterOption.default_template_id) {
    context.default_template_id = encounterOption.default_template_id;
  }
  if (encounterOption.default_template_name) {
    context.default_template_name = encounterOption.default_template_name;
  }

  return context;
}

/**
 * Build context data from current state (used for template/note type selection)
 * @param {Object} currentEncounter - The current encounter state object
 * @param {Object} currentPatient - The current patient state object
 * @param {Object} extra - Additional fields to include (e.g., template_id)
 * @returns {Object} Context data with encounter_id, patient_id, patient_name, and extra fields
 */
export function buildContextFromState(currentEncounter, currentPatient, extra = {}) {
  const context: Record<string, unknown> = { ...extra };
  
  if (currentEncounter?.id) {
    context.encounter_id = currentEncounter.id;
  }
  if (currentPatient?.id) {
    context.patient_id = currentPatient.id;
  }
  if (currentPatient?.name) {
    context.patient_name = currentPatient.name;
  }
  
  return context;
}

/**
 * Build state objects for setCurrentEncounter and setCurrentPatient
 * from an encounter option (used when selecting from dropdown)
 * @param {Object} encounterOption - The encounter option from the dropdown
 * @returns {Object} Object with encounter and patient state objects
 */
export function buildStateFromEncounterOption(encounterOption) {
  if (!encounterOption) {
    return { encounter: null, patient: null };
  }
  
  const encounter = {
    id: encounterOption.encounter_id,
    patient_id: encounterOption.patient_id || null,
    patient_name: encounterOption.patient_name || null,
  };
  
  const patient = encounterOption.patient_id ? {
    id: encounterOption.patient_id,
    name: encounterOption.patient_name || null,
  } : null;
  
  return { encounter, patient };
}
